import pdfMake from "pdfmake/build/pdfmake";
import htmlToPdfmake from "html-to-pdfmake";
import {
    Document,
    Packer,
    Paragraph,
    TextRun,
    HeadingLevel,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    ShadingType,
    ImageRun,
    Header,
    AlignmentType,
    ExternalHyperlink
} from "docx";
import uplLogoSrc from "../assets/images/upl-logo.png";
import NotoSansRegular from "../assets/fonts/NotoSans-Regular.js";
import NotoSansBold from "../assets/fonts/NotoSans-Bold.js";
import { getNormalizedCareerTimeline } from "./talentPulseUtils";

// ─── Logo helpers ─────────────────────────────────────────────────────────────

// UPL logo natural dimensions (311 × 124 px)
const UPL_LOGO_NATURAL_W = 311;
const UPL_LOGO_NATURAL_H = 124;

// Font used across all DOCX text runs
const DOCX_FONT = "Noto Sans";

/** Fetches a URL and returns a base64 data-URL string (for PDF). */
const fetchAsDataUrl = async (src) => {
    const res = await fetch(src);
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

/** Fetches a URL and returns an ArrayBuffer (for DOCX ImageRun). */
const fetchAsArrayBuffer = async (src) => {
    const res = await fetch(src);
    return res.arrayBuffer();
};

/**
 * Exports the given HTML element as a PDF file.
 * @param {HTMLElement} element - The DOM element to export.
 * @param {string} filename - Output filename (without extension).
 */
export const exportToPDF = async (element, filename = "response") => {
    if (!element) return;

    pdfMake.addVirtualFileSystem({
        "NotoSans-Regular.ttf": NotoSansRegular,
        "NotoSans-Bold.ttf": NotoSansBold
    });

    pdfMake.setFonts({
        NotoSans: {
            normal: "NotoSans-Regular.ttf",
            bold: "NotoSans-Bold.ttf",
            italics: "NotoSans-Regular.ttf",
            bolditalics: "NotoSans-Bold.ttf"
        },
        Roboto: {
            normal: "NotoSans-Regular.ttf",
            bold: "NotoSans-Bold.ttf",
            italics: "NotoSans-Regular.ttf",
            bolditalics: "NotoSans-Bold.ttf"
        }
    });

    let uplLogoDataUrl = null;
    try { uplLogoDataUrl = await fetchAsDataUrl(uplLogoSrc); } catch (_) { /* skip */ }

    // ── Clone & render content ──
    const clone = element.cloneNode(true);
    clone.querySelectorAll(".markdown-copy-button").forEach((btn) => btn.remove());

    // Remove any <header> / <footer> elements so they don't bleed into PDF body text
    clone.querySelectorAll("header, footer, nav").forEach((el) => el.remove());

    // Strip inline font-sizes & large paddings that cause content to appear zoomed
    clone.querySelectorAll("*").forEach((el) => {
        el.style.fontSize = "";
        el.style.lineHeight = "";
        // Reset large padding/margin that inflates whitespace
        const cs = window.getComputedStyle(el);
        if (parseFloat(cs.paddingLeft) > 20) el.style.padding = "";
        if (parseFloat(cs.marginTop) > 20) el.style.marginTop = "";
    });

    // Table style polish
    clone.querySelectorAll("table").forEach((table) => {
        table.style.borderCollapse = "collapse";
        table.style.width = "100%";
    });
    clone.querySelectorAll("th, td").forEach((cell) => {
        cell.style.border = "1px solid #aaaaaa";
        cell.style.padding = "6px 8px";
        cell.style.textAlign = "left";
        cell.style.fontSize = "";
    });
    clone.querySelectorAll("th").forEach((th) => {
        th.style.backgroundColor = "#e8e8e8";
        th.style.fontWeight = "bold";
    });

    // Strip emojis — pdfmake's NotoSans doesn't have emoji glyphs
    const stripEmojis = (str) =>
        str.replace(/([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2600-\u26FF]|\uD83E[\uDD10-\uDDFF])/g, " ");

    const rawHtml = stripEmojis(clone.innerHTML);

    const parsedPdfMakeSchema = htmlToPdfmake(rawHtml, {
        window: window,
        tableAutoSize: true,
        defaultStyles: {
            a: {
                color: "blue",
                decoration: "underline"
            },
            b: { bold: true },
            strong: { bold: true },
            h1: { fontSize: 18, bold: true, marginBottom: 8 },
            h2: { fontSize: 15, bold: true, marginBottom: 6 },
            h3: { fontSize: 12, bold: true, marginBottom: 4 },
            p: { fontSize: 10, marginBottom: 5 },
            li: { fontSize: 10, marginBottom: 3 },
            table: { margin: [0, 5, 0, 15] },
            pre: { margin: [0, 5, 0, 15], fillColor: "#f5f5f5", fontSize: 9 },
            code: { fontSize: 9, color: "#333333" }
        }
    });

    // Create the final Document Definition for pdfMake
    const docDefinition = {
        info: {
            title: filename,
        },
        pageSize: 'A4',
        pageMargins: [40, 80, 40, 40], // [left, top, right, bottom]
        header: function (currentPage, pageCount, pageSize) {
            if (uplLogoDataUrl) {
                // Return a table/columns to position the logo top right
                return {
                    margin: [40, 10, 40, 0], // outer header margin [left, top, right, bottom]
                    columns: [
                        { text: '', width: '*' }, // push logo to the right
                        {
                            image: uplLogoDataUrl,
                            width: 70,
                            margin: [0, 0, 0, 0]
                        }
                    ]
                };
            }
            return null;
        },
        content: parsedPdfMakeSchema,
        defaultStyle: {
            font: 'NotoSans',
            fontSize: 10,
            lineHeight: 1.3
        }
    };

    pdfMake.createPdf(docDefinition).download(`${filename}.pdf`);
};

// ─── DOCX Helpers ─────────────────────────────────────────────────────────────

const CELL_BORDER = {
    top: { style: BorderStyle.SINGLE, size: 6, color: "AAAAAA" },
    bottom: { style: BorderStyle.SINGLE, size: 6, color: "AAAAAA" },
    left: { style: BorderStyle.SINGLE, size: 6, color: "AAAAAA" },
    right: { style: BorderStyle.SINGLE, size: 6, color: "AAAAAA" },
};

/** Build inline TextRun(s) from a single inline element or text node. */
const inlineRuns = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        return text ? [new TextRun({ text, size: 22, font: DOCX_FONT })] : [];
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return [];

    const tag = node.tagName.toLowerCase();
    const text = node.innerText || node.textContent || "";

    if (tag === "strong" || tag === "b")
        return [new TextRun({ text, bold: true, size: 22, font: DOCX_FONT })];
    if (tag === "em" || tag === "i")
        return [new TextRun({ text, italics: true, size: 22, font: DOCX_FONT })];
    if (tag === "code")
        return [new TextRun({ text, font: "Courier New", size: 20 })];
    if (tag === "br")
        return [new TextRun({ text: "", break: 1 })];

    if (tag === "a") {
        const href = node.getAttribute("href");
        return [
            new ExternalHyperlink({
                link: href,
                children: [
                    new TextRun({
                        text: text,
                        style: "Hyperlink",
                        font: DOCX_FONT,
                        size: 22
                    })
                ]
            })
        ];
    }

    const runs = [];
    node.childNodes.forEach((child) => runs.push(...inlineRuns(child)));
    return runs;
};

/** Collect all inline runs from a container element (e.g. <td>, <p>). */
const collectRuns = (container) => {
    const runs = [];
    container.childNodes.forEach((child) => runs.push(...inlineRuns(child)));
    return runs.length > 0 ? runs : [new TextRun({ text: container.innerText || container.textContent || "", size: 22, font: DOCX_FONT })];
};

/** Convert an HTML <table> element to a docx Table. */
const buildDocxTable = (tableEl) => {
    const rows = [];

    tableEl.querySelectorAll("tr").forEach((tr, rowIdx) => {
        const cells = [];
        const isHeaderRow = tr.closest("thead") !== null || tr.querySelectorAll("th").length > 0;

        tr.querySelectorAll("th, td").forEach((cell) => {
            const isHeader = cell.tagName.toLowerCase() === "th" || isHeaderRow;
            const cellText = cell.innerText || cell.textContent || "";

            // Header cells: single bold run from plain text
            // Data cells: rich inline runs preserving bold/italic/code
            const cellRuns = isHeader
                ? [new TextRun({ text: cellText, bold: true, size: 22, font: DOCX_FONT })]
                : collectRuns(cell);

            cells.push(
                new TableCell({
                    children: [new Paragraph({ children: cellRuns, spacing: { after: 60 } })],
                    borders: CELL_BORDER,
                    shading: isHeader
                        ? { type: ShadingType.CLEAR, fill: "E8E8E8" }
                        : { type: ShadingType.CLEAR, fill: "FFFFFF" },
                    margins: { top: 80, bottom: 80, left: 120, right: 120 },
                })
            );
        });

        if (cells.length > 0) {
            rows.push(new TableRow({ children: cells, tableHeader: isHeaderRow && rowIdx === 0 }));
        }
    });

    if (rows.length === 0) return null;

    return new Table({
        rows,
        width: { size: 100, type: WidthType.PERCENTAGE },
    });
};

// ─── Main DOCX Parser ─────────────────────────────────────────────────────────

/**
 * Recursively converts an HTML element tree into an array of docx block elements
 * (Paragraph | Table). Tables are fully preserved.
 */
const parseNodeToDocxBlocks = (node) => {
    const blocks = [];

    if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.trim();
        if (text) {
            blocks.push(new Paragraph({ children: [new TextRun({ text, size: 22, font: DOCX_FONT })], spacing: { after: 100 } }));
        }
        return blocks;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return blocks;

    const tag = node.tagName.toLowerCase();

    // ── Headings ──
    if (tag === "h1") {
        blocks.push(new Paragraph({ children: [new TextRun({ text: node.innerText || node.textContent, font: DOCX_FONT, size: 32, bold: true })], heading: HeadingLevel.HEADING_1, spacing: { after: 200 } }));
        return blocks;
    }
    if (tag === "h2") {
        blocks.push(new Paragraph({ children: [new TextRun({ text: node.innerText || node.textContent, font: DOCX_FONT, size: 28, bold: true })], heading: HeadingLevel.HEADING_2, spacing: { after: 160 } }));
        return blocks;
    }
    if (tag === "h3") {
        blocks.push(new Paragraph({ children: [new TextRun({ text: node.innerText || node.textContent, font: DOCX_FONT, size: 24, bold: true })], heading: HeadingLevel.HEADING_3, spacing: { after: 140 } }));
        return blocks;
    }
    if (tag === "h4" || tag === "h5" || tag === "h6") {
        blocks.push(new Paragraph({ children: [new TextRun({ text: node.innerText || node.textContent, font: DOCX_FONT, size: 22, bold: true })], heading: HeadingLevel.HEADING_4, spacing: { after: 120 } }));
        return blocks;
    }

    // ── Paragraph ──
    if (tag === "p") {
        const runs = collectRuns(node);
        if (runs.length > 0) {
            blocks.push(new Paragraph({ children: runs, spacing: { after: 120 } }));
        }
        return blocks;
    }

    // ── Lists ──
    if (tag === "ul" || tag === "ol") {
        node.querySelectorAll(":scope > li").forEach((li, idx) => {
            const prefix = tag === "ol" ? `${idx + 1}. ` : "• ";
            const runs = collectRuns(li);
            blocks.push(
                new Paragraph({
                    children: [new TextRun({ text: prefix, size: 22, font: DOCX_FONT, bold: false }), ...runs],
                    indent: { left: 360 },
                    spacing: { after: 80 },
                })
            );
        });
        return blocks;
    }

    // ── Code block ──
    if (tag === "pre") {
        const text = node.innerText || node.textContent || "";
        // Split by newline to preserve code lines
        text.split("\n").forEach((line) => {
            blocks.push(
                new Paragraph({
                    children: [new TextRun({ text: line || " ", font: "Courier New", size: 20 })],
                    spacing: { after: 0 },
                })
            );
        });
        blocks.push(new Paragraph({ children: [new TextRun({ text: " " })], spacing: { after: 120 } }));
        return blocks;
    }

    // ── Table ──
    if (tag === "table") {
        const table = buildDocxTable(node);
        if (table) {
            blocks.push(table);
            // Add spacing after table
            blocks.push(new Paragraph({ children: [new TextRun("")], spacing: { after: 160 } }));
        }
        return blocks;
    }

    // ── Horizontal rule ──
    if (tag === "hr") {
        blocks.push(new Paragraph({ children: [new TextRun({ text: "─".repeat(60), font: DOCX_FONT })], spacing: { after: 120 } }));
        return blocks;
    }

    // ── Line break ──
    if (tag === "br") {
        blocks.push(new Paragraph({ children: [new TextRun({ text: " " })], spacing: { after: 60 } }));
        return blocks;
    }

    // ── thead / tbody / tfoot — skip (handled inside table) ──
    if (tag === "thead" || tag === "tbody" || tag === "tfoot") return blocks;

    // ── Generic container (div, span, section, article, etc.) — recurse ──
    node.childNodes.forEach((child) => blocks.push(...parseNodeToDocxBlocks(child)));
    return blocks;
};

// ─── DOCX Export ──────────────────────────────────────────────────────────────

/**
 * Exports the given HTML element as a DOCX file, preserving tables.
 * @param {HTMLElement} element - The DOM element to export.
 * @param {string} filename - Output filename (without extension).
 */
export const exportToDOCX = async (element, filename = "response") => {
    if (!element) return;

    // ── Fetch UPL logo for header ──
    let uplLogoBuffer = null;
    try { uplLogoBuffer = await fetchAsArrayBuffer(uplLogoSrc); } catch (_) { /* skip */ }

    // ── Parse content ──
    const clone = element.cloneNode(true);
    clone.querySelectorAll(".markdown-copy-button").forEach((btn) => btn.remove());

    const blocks = [];
    clone.childNodes.forEach((child) => blocks.push(...parseNodeToDocxBlocks(child)));

    const finalBlocks =
        blocks.length > 0
            ? blocks
            : [new Paragraph({ children: [new TextRun({ text: clone.innerText || clone.textContent || "", size: 22, font: DOCX_FONT })] })];

    // ── Build header: UPL logo top-right ──
    // docx v9: transformation width/height are in PIXELS
    // UPL logo: 90 × 36 px (aspect 311:124)
    const UPL_PX_W = 90;
    const UPL_PX_H = Math.round(UPL_PX_W * (UPL_LOGO_NATURAL_H / UPL_LOGO_NATURAL_W)); // ≈ 36 px

    const docHeader = new Header({
        children: [
            new Paragraph({
                children: uplLogoBuffer
                    ? [new ImageRun({ data: uplLogoBuffer, transformation: { width: UPL_PX_W, height: UPL_PX_H }, type: "png" })]
                    : [new TextRun({ text: "UPL", bold: true, size: 22, font: DOCX_FONT })],
                alignment: AlignmentType.RIGHT,
                spacing: { after: 80 },
            }),
        ],
    });

    const doc = new Document({
        sections: [
            {
                properties: {},
                headers: { default: docHeader },
                children: finalBlocks,
            },
        ],
    });

    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.docx`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
};

/**
 * Exports a candidate profile card layout to PDF with exact matching layout.
 * @param {Object} data - Candidate profile data.
 * @param {string} filename - Output filename (without extension).
 */
export const exportProfileCardToPDF = async (data, filename = "profile") => {
    pdfMake.addVirtualFileSystem({
        "NotoSans-Regular.ttf": NotoSansRegular,
        "NotoSans-Bold.ttf": NotoSansBold
    });

    pdfMake.setFonts({
        NotoSans: {
            normal: "NotoSans-Regular.ttf",
            bold: "NotoSans-Bold.ttf",
            italics: "NotoSans-Regular.ttf",
            bolditalics: "NotoSans-Bold.ttf"
        }
    });

    let uplLogoDataUrl = null;
    try { uplLogoDataUrl = await fetchAsDataUrl(uplLogoSrc); } catch (_) {}

    const stripEmojis = (str) =>
        (str || "").replace(/([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2600-\u26FF]|\uD83E[\uDD10-\uDDFF])/g, " ").trim();

    const summaryItems = data.summaryItems || [];
    const profile = data.profile || {};

    const docDefinition = {
        info: { title: filename },
        pageSize: 'A4',
        pageMargins: [35, 35, 35, 35],
        content: [
            // Header with Candidate Identity and UPL Logo
            {
                columns: [
                    {
                        width: '*',
                        stack: [
                            {
                                text: [
                                    { text: stripEmojis(data.name) + '  ', fontSize: 18, bold: true, color: '#15264e' },
                                    { text: ' CANDIDATE ', fontSize: 8, bold: true, color: '#0284c7', background: '#e0f2fe' },
                                    data.candidateId ? { text: `  ${data.candidateId}`, fontSize: 9, bold: true, color: '#61708e' } : ''
                                ]
                            },
                            { text: stripEmojis(profile.role), fontSize: 11, color: '#52617e', marginTop: 4 },
                            { text: stripEmojis(profile.location), fontSize: 10, color: '#8994ad', marginTop: 2 }
                        ]
                    },
                    uplLogoDataUrl ? { image: uplLogoDataUrl, width: 65, alignment: 'right' } : { text: '' }
                ],
                marginBottom: 16
            },

            // 5-Column Summary Grid Cards
            {
                table: {
                    widths: ['*', '*', '*', '*', '*'],
                    body: [
                        summaryItems.map(item => ({
                            text: (item.label || '').toUpperCase(),
                            fontSize: 8,
                            bold: true,
                            color: '#62718c',
                            fillColor: '#f7f8fc',
                            border: [false, false, false, false],
                            alignment: 'left'
                        })),
                        summaryItems.map(item => ({
                            text: stripEmojis(item.value),
                            fontSize: 9,
                            bold: true,
                            color: '#13244a',
                            fillColor: '#f7f8fc',
                            border: [false, false, false, false],
                            alignment: 'left',
                            marginTop: 2
                        }))
                    ]
                },
                layout: {
                    defaultBorder: false,
                    paddingLeft: () => 8,
                    paddingRight: () => 8,
                    paddingTop: () => 6,
                    paddingBottom: () => 6
                },
                marginBottom: 16
            },

            // Career Journey Section
            { text: 'CAREER JOURNEY', fontSize: 10, bold: true, color: '#62718c', marginBottom: 6 },
            {
                table: {
                    widths: ['*'],
                    body: [
                        [{
                            stack: (() => {
                                const rawTimeline = profile.career_timeline || profile.career;
                                const timeline = getNormalizedCareerTimeline(rawTimeline, profile.currentOrg || profile.current);
                                if (Array.isArray(timeline) && timeline.length > 0) {
                                    return timeline.flatMap((item, index) => {
                                        const comp = item.company || item.organization || item.org || '';
                                        const r = item.role || item.designation || '';
                                        const ten = item.tenure || item.duration || '';
                                        const label = item.label;
                                        const detailText = [r, comp, ten].filter(Boolean).join(' — ');
                                        return [
                                            { text: label, fontSize: 8, bold: true, color: '#62718c' },
                                            { text: stripEmojis(detailText || '—'), fontSize: 9, color: '#13244a', marginTop: 2, marginBottom: index === timeline.length - 1 ? 0 : 6 }
                                        ];
                                    });
                                }
                                return [
                                    { text: 'Current org', fontSize: 8, bold: true, color: '#62718c' },
                                    { text: stripEmojis(profile.currentOrg), fontSize: 9, color: '#13244a', marginTop: 2, marginBottom: 6 },
                                    { text: 'Prev organisation', fontSize: 8, bold: true, color: '#62718c' },
                                    { text: stripEmojis(profile.prevOrg || profile.previousOrg || '—'), fontSize: 9, color: '#13244a', marginTop: 2 }
                                ];
                            })(),
                            fillColor: '#f7f8fc',
                            border: [false, false, false, false]
                        }]
                    ]
                },
                layout: { paddingLeft: () => 10, paddingRight: () => 10, paddingTop: () => 8, paddingBottom: () => 8 },
                marginBottom: 16
            },

            // 2-Column Section: Insights | Strategy
            {
                columns: [
                    {
                        width: '50%',
                        marginRight: 8,
                        stack: [
                            { text: 'INSIGHTS FROM INTERVIEW', fontSize: 10, bold: true, color: '#62718c', marginBottom: 6 },
                            {
                                ul: (profile.insights || []).map(item => stripEmojis(item)),
                                fontSize: 9,
                                color: '#13244a'
                            }
                        ]
                    },
                    {
                        width: '50%',
                        marginLeft: 8,
                        stack: [
                            { text: 'STRATEGY & APPROACH', fontSize: 10, bold: true, color: '#62718c', marginBottom: 6 },
                            {
                                ul: (profile.strategy || []).map(item => stripEmojis(item)),
                                fontSize: 9,
                                color: '#13244a'
                            }
                        ]
                    }
                ],
                marginBottom: 16
            },

            // 2-Column Section: Compensation | Status & Next Step
            {
                columns: [
                    {
                        width: '50%',
                        marginRight: 8,
                        stack: [
                            { text: 'COMPENSATION', fontSize: 10, bold: true, color: '#62718c', marginBottom: 6 },
                            {
                                table: {
                                    widths: ['*', '*'],
                                    body: [
                                        [
                                            { text: 'Current CTC', fontSize: 9, color: '#52617e', border: [false, false, false, false] },
                                            { text: '—', fontSize: 9, bold: true, color: '#13244a', border: [false, false, false, false], alignment: 'right' }
                                        ],
                                        [
                                            { text: 'Expected', fontSize: 9, color: '#52617e', border: [false, false, false, false] },
                                            { text: '—', fontSize: 9, bold: true, color: '#13244a', border: [false, false, false, false], alignment: 'right' }
                                        ]
                                    ]
                                },
                                layout: { defaultBorder: false, paddingTop: () => 2, paddingBottom: () => 2 }
                            }
                        ]
                    },
                    {
                        width: '50%',
                        marginLeft: 8,
                        stack: [
                            { text: 'STATUS & NEXT STEP', fontSize: 10, bold: true, color: '#62718c', marginBottom: 6 },
                            {
                                table: {
                                    widths: ['*', '*'],
                                    body: [
                                        [
                                            { text: 'Status', fontSize: 9, color: '#52617e', border: [false, false, false, false] },
                                            { text: stripEmojis(data.status || '—'), fontSize: 9, bold: true, color: '#13244a', border: [false, false, false, false], alignment: 'right' }
                                        ],
                                        [
                                            { text: 'Next', fontSize: 9, color: '#52617e', border: [false, false, false, false] },
                                            { text: stripEmojis(profile.nextStep || '—'), fontSize: 9, bold: true, color: '#13244a', border: [false, false, false, false], alignment: 'right' }
                                        ]
                                    ]
                                },
                                layout: { defaultBorder: false, paddingTop: () => 2, paddingBottom: () => 2 }
                            }
                        ]
                    }
                ],
                marginBottom: 16
            },

            // Source Section
            { text: 'SOURCE', fontSize: 10, bold: true, color: '#62718c', marginBottom: 2 },
            { text: stripEmojis(data.source || 'Not available'), fontSize: 9, color: '#334155' }
        ],
        defaultStyle: {
            font: 'NotoSans',
            fontSize: 9,
            lineHeight: 1.3
        }
    };

    pdfMake.createPdf(docDefinition).download(`${filename}.pdf`);
};
